const SESSION_SCOPED_KEYS = new Set([
  'access_token',
  'token_type',
  'authId',
  'ID',
  'client_id',
  'user_type',
  'name',
  'displayname',
  'profile_name',
  'logo_url',
  'logo',
  'IsLicenseBase',
  'IsAgentBased',
  'EnvironmentType',
  'Type',
  'client_type',
  'verification_status',
  'EscalationStatus',
  'timezone_id',
  'merge_timezone_ids',
  'assigned_locations',
  'Location',
  'AssignClient',
  'ShareLocation',
  'selectedLocation',
  'selectedProperty',
  'theme_mode',
  'remember_me_auth',
  'authLogin',
  'userEmail',
  'otp_expiry_time',
  'otp_session_expiry_time'
]);

const AUTH_CLUSTER_KEY = 'auth_cluster_id';
const LOGOUT_SYNC_KEY = 'auth_cluster_logout_event';
const AUTH_HANDOFF_KEY = 'auth_session_handoff';
const LAST_CLOSED_SESSION_KEY = 'auth_last_closed_session';
const LAST_LOGGED_IN_SESSION_KEY = 'auth_last_logged_in_session';
const ACTIVE_TABS_KEY = 'auth_active_tabs';
const TAB_INSTANCE_KEY = 'auth_tab_instance_id';
const TAB_HEARTBEAT_MS = 5000;
const TAB_STALE_MS = 30000;
const createId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const isSessionScopedKey = (key) => SESSION_SCOPED_KEYS.has(String(key));
const hasRememberedRestartPreference = () => sessionStorage.getItem('remember_me_auth') === '1';
const isSameOrigin = (url) => {
  try {
    return new URL(url).origin === window.location.origin;
  } catch (error) {
    return false;
  }
};
const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};
const ensureAuthClusterId = () => {
  let clusterId = sessionStorage.getItem(AUTH_CLUSTER_KEY);
  if (clusterId) return clusterId;

  clusterId = createId();
  sessionStorage.setItem(AUTH_CLUSTER_KEY, clusterId);
  return clusterId;
};

const ensureTabInstanceId = () => {
  let tabId = sessionStorage.getItem(TAB_INSTANCE_KEY);
  if (tabId) return tabId;
  tabId = createId();
  sessionStorage.setItem(TAB_INSTANCE_KEY, tabId);
  return tabId;
};

const readActiveTabs = () => {
  const parsed = safeParse(localStorage.getItem(ACTIVE_TABS_KEY));
  if (!parsed || typeof parsed !== 'object') return {};
  return parsed;
};

const writeActiveTabs = (tabs) => {
  localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(tabs));
};

const pruneActiveTabs = (tabs, now = Date.now()) => {
  const nextTabs = { ...tabs };
  Object.keys(nextTabs).forEach((tabId) => {
    if (now - Number(nextTabs[tabId] || 0) > TAB_STALE_MS) {
      delete nextTabs[tabId];
    }
  });
  return nextTabs;
};

const touchActiveTab = () => {
  const tabId = ensureTabInstanceId();
  const tabs = pruneActiveTabs(readActiveTabs());
  tabs[tabId] = Date.now();
  writeActiveTabs(tabs);
};

const removeActiveTab = () => {
  const tabId = ensureTabInstanceId();
  const tabs = pruneActiveTabs(readActiveTabs());
  if (!tabs[tabId]) return;
  delete tabs[tabId];
  writeActiveTabs(tabs);
};

const hasOtherActiveTabs = () => {
  const tabId = ensureTabInstanceId();
  const tabs = pruneActiveTabs(readActiveTabs());
  writeActiveTabs(tabs);
  return Object.keys(tabs).some((id) => id !== tabId);
};

const installActiveTabPresence = () => {
  touchActiveTab();

  const heartbeat = setInterval(() => {
    touchActiveTab();
  }, TAB_HEARTBEAT_MS);

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      touchActiveTab();
    }
  });
  window.addEventListener('beforeunload', removeActiveTab);
  window.addEventListener('pagehide', removeActiveTab);

  return () => clearInterval(heartbeat);
};

const clearSessionScopedStorageInternal = () => {
  SESSION_SCOPED_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  sessionStorage.removeItem(AUTH_CLUSTER_KEY);
  localStorage.removeItem(LAST_CLOSED_SESSION_KEY);
  localStorage.removeItem(LAST_LOGGED_IN_SESSION_KEY);
};

const clearPersistedRestartSessions = () => {
  localStorage.removeItem(LAST_CLOSED_SESSION_KEY);
  localStorage.removeItem(LAST_LOGGED_IN_SESSION_KEY);
};

const getCurrentAuthIdentity = () => ({
  access_token: sessionStorage.getItem('access_token'),
  ID: sessionStorage.getItem('ID'),
  client_id: sessionStorage.getItem('client_id'),
  user_type: sessionStorage.getItem('user_type')
});

const hasMatchingAuthIdentity = (sourceAuth, targetAuth) => {
  if (!sourceAuth || !targetAuth) return false;

  const sourceId = String(sourceAuth.ID || '');
  const targetId = String(targetAuth.ID || '');
  const sourceClientId = String(sourceAuth.client_id || '');
  const targetClientId = String(targetAuth.client_id || '');
  const sourceUserType = String(sourceAuth.user_type || '');
  const targetUserType = String(targetAuth.user_type || '');

  if (sourceId && targetId && sourceClientId && targetClientId) {
    return sourceId === targetId &&
      sourceClientId === targetClientId &&
      sourceUserType === targetUserType;
  }

  return Boolean(sourceAuth.access_token) && sourceAuth.access_token === targetAuth.access_token;
};

const publishLogoutSync = () => {
  const clusterId = sessionStorage.getItem(AUTH_CLUSTER_KEY);
  if (!clusterId) return;

  localStorage.setItem(
    LOGOUT_SYNC_KEY,
    JSON.stringify({
      clusterId,
      at: Date.now(),
      id: createId(),
      auth: getCurrentAuthIdentity()
    })
  );
};

export const getAppStorageItem = (key) => {
  const normalizedKey = String(key);
  if (!isSessionScopedKey(normalizedKey)) {
    return localStorage.getItem(normalizedKey);
  }

  const sessionValue = sessionStorage.getItem(normalizedKey);
  if (sessionValue !== null) return sessionValue;

  return localStorage.getItem(normalizedKey);
};

export const setAppStorageItem = (key, value) => {
  const normalizedKey = String(key);
  if (!isSessionScopedKey(normalizedKey)) {
    localStorage.setItem(normalizedKey, value);
    return;
  }

  ensureAuthClusterId();
  sessionStorage.setItem(normalizedKey, value);
  localStorage.removeItem(normalizedKey);
};

export const removeAppStorageItem = (key) => {
  const normalizedKey = String(key);
  if (!isSessionScopedKey(normalizedKey)) {
    localStorage.removeItem(normalizedKey);
    return;
  }

  sessionStorage.removeItem(normalizedKey);
  localStorage.removeItem(normalizedKey);
};

export const clearSessionScopedStorage = () => {
  publishLogoutSync();
  clearSessionScopedStorageInternal();
};

const migrateLegacyAuthToSessionStorage = () => {
  SESSION_SCOPED_KEYS.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key);
    const localValue = localStorage.getItem(key);

    if (sessionValue === null && localValue !== null) {
      sessionStorage.setItem(key, localValue);
    }

    if (localValue !== null) {
      localStorage.removeItem(key);
    }
  });
};

const adoptSessionFromOpener = () => {
  if (sessionStorage.getItem('access_token')) return;
  if (!window.opener || !isSameOrigin(document.referrer)) return;

  try {
    const openerSession = window.opener.sessionStorage;
    if (!openerSession) return;

    let hasCopiedAuth = false;
    SESSION_SCOPED_KEYS.forEach((key) => {
      const value = openerSession.getItem(key);
      if (value !== null) {
        sessionStorage.setItem(key, value);
        hasCopiedAuth = hasCopiedAuth || key === 'access_token';
      }
    });

    const openerClusterId = openerSession.getItem(AUTH_CLUSTER_KEY);
    if (openerClusterId) {
      sessionStorage.setItem(AUTH_CLUSTER_KEY, openerClusterId);
    } else if (hasCopiedAuth) {
      ensureAuthClusterId();
    }
  } catch (error) {
    // Ignore opener access failures and continue normal login flow.
  }
};

const installLogoutSyncListener = () => {
  window.addEventListener('storage', (event) => {
    if (event.key !== LOGOUT_SYNC_KEY || !event.newValue) return;

    const payload = safeParse(event.newValue);
    const currentClusterId = sessionStorage.getItem(AUTH_CLUSTER_KEY);
    if (!payload?.clusterId || !currentClusterId) return;
    if (payload.clusterId !== currentClusterId) return;
    if (!hasMatchingAuthIdentity(payload.auth, getCurrentAuthIdentity())) return;

    clearSessionScopedStorageInternal();
    window.location.replace('/login');
  });
};

export const prepareSessionHandoff = (ttlMs = 20000) => {
  const accessToken = sessionStorage.getItem('access_token');
  if (!accessToken) return;

  const sessionData = {};
  SESSION_SCOPED_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value !== null) {
      sessionData[key] = value;
    }
  });

  localStorage.setItem(
    AUTH_HANDOFF_KEY,
    JSON.stringify({
      id: createId(),
      expiresAt: Date.now() + ttlMs,
      clusterId: ensureAuthClusterId(),
      sessionData
    })
  );
};

const persistLastClosedSession = () => {
  const accessToken = sessionStorage.getItem('access_token');
  const authId = parseInt(sessionStorage.getItem('authId') || '0', 10);
  if (!accessToken || authId !== 1) return;
  if (!hasRememberedRestartPreference()) {
    localStorage.removeItem(LAST_CLOSED_SESSION_KEY);
    return;
  }

  const sessionData = {};
  SESSION_SCOPED_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value !== null) {
      sessionData[key] = value;
    }
  });

  localStorage.setItem(
    LAST_CLOSED_SESSION_KEY,
    JSON.stringify({
      id: createId(),
      at: Date.now(),
      clusterId: ensureAuthClusterId(),
      rememberMe: true,
      sessionData
    })
  );
};

export const setRememberedRestartPreference = (shouldRemember) => {
  const nextValue = shouldRemember ? '1' : '0';
  sessionStorage.setItem('remember_me_auth', nextValue);

  if (!shouldRemember) {
    clearPersistedRestartSessions();
  }
};

export const persistLastLoggedInSession = (shouldRemember = hasRememberedRestartPreference()) => {
  const accessToken = sessionStorage.getItem('access_token');
  const authId = parseInt(sessionStorage.getItem('authId') || '0', 10);
  if (!accessToken || authId !== 1) return;
  if (!shouldRemember) {
    clearPersistedRestartSessions();
    return;
  }

  const sessionData = {};
  SESSION_SCOPED_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value !== null) {
      sessionData[key] = value;
    }
  });

  localStorage.setItem(
    LAST_LOGGED_IN_SESSION_KEY,
    JSON.stringify({
      id: createId(),
      at: Date.now(),
      clusterId: ensureAuthClusterId(),
      rememberMe: true,
      sessionData
    })
  );
};

const adoptSessionFromSnapshot = (payload) => {
  if (!payload?.sessionData) return false;
  if (payload.rememberMe !== true) return false;

  const hasToken = Boolean(payload.sessionData.access_token);
  const hasActiveAuth = parseInt(payload.sessionData.authId || '0', 10) === 1;
  if (!hasToken || !hasActiveAuth) return false;

  SESSION_SCOPED_KEYS.forEach((key) => {
    const value = payload.sessionData[key];
    if (typeof value !== 'undefined' && value !== null) {
      sessionStorage.setItem(key, value);
    }
  });

  if (payload.clusterId) {
    sessionStorage.setItem(AUTH_CLUSTER_KEY, payload.clusterId);
  } else if (sessionStorage.getItem('access_token')) {
    ensureAuthClusterId();
  }

  return true;
};

const adoptSessionFromLastLoggedIn = () => {
  if (sessionStorage.getItem('access_token')) return;
  if (hasOtherActiveTabs()) return;
  const payload = safeParse(localStorage.getItem(LAST_LOGGED_IN_SESSION_KEY));
  adoptSessionFromSnapshot(payload);
};

const adoptSessionFromLastClosed = () => {
  if (sessionStorage.getItem('access_token')) return;

  const payload = safeParse(localStorage.getItem(LAST_CLOSED_SESSION_KEY));
  adoptSessionFromSnapshot(payload);
};

const adoptSessionFromHandoff = () => {
  if (sessionStorage.getItem('access_token')) return;

  const handoffPayload = safeParse(localStorage.getItem(AUTH_HANDOFF_KEY));
  if (!handoffPayload?.sessionData) return;

  if (typeof handoffPayload.expiresAt !== 'number' || Date.now() > handoffPayload.expiresAt) {
    localStorage.removeItem(AUTH_HANDOFF_KEY);
    return;
  }

  SESSION_SCOPED_KEYS.forEach((key) => {
    const value = handoffPayload.sessionData[key];
    if (typeof value !== 'undefined' && value !== null) {
      sessionStorage.setItem(key, value);
    }
  });

  if (handoffPayload.clusterId) {
    sessionStorage.setItem(AUTH_CLUSTER_KEY, handoffPayload.clusterId);
  } else if (sessionStorage.getItem('access_token')) {
    ensureAuthClusterId();
  }
};

export const installStorageProxy = () => {
  if (typeof window === 'undefined' || window.__vidiskyStorageProxyInstalled) return;

  installActiveTabPresence();
  migrateLegacyAuthToSessionStorage();
  adoptSessionFromHandoff();
  adoptSessionFromOpener();
  adoptSessionFromLastLoggedIn();
  adoptSessionFromLastClosed();
  installLogoutSyncListener();
  window.addEventListener('pagehide', persistLastClosedSession);

  const originalGetItem = Storage.prototype.getItem;
  const originalSetItem = Storage.prototype.setItem;
  const originalRemoveItem = Storage.prototype.removeItem;

  Storage.prototype.getItem = function patchedGetItem(key) {
    const normalizedKey = String(key);

    if (this === localStorage && isSessionScopedKey(normalizedKey)) {
      const sessionValue = originalGetItem.call(sessionStorage, normalizedKey);
      if (sessionValue !== null) return sessionValue;
    }

    return originalGetItem.call(this, normalizedKey);
  };

  Storage.prototype.setItem = function patchedSetItem(key, value) {
    const normalizedKey = String(key);

    if (this === localStorage && isSessionScopedKey(normalizedKey)) {
      ensureAuthClusterId();
      originalSetItem.call(sessionStorage, normalizedKey, value);
      originalRemoveItem.call(localStorage, normalizedKey);
      return;
    }

    originalSetItem.call(this, normalizedKey, value);
  };

  Storage.prototype.removeItem = function patchedRemoveItem(key) {
    const normalizedKey = String(key);

    if (this === localStorage && isSessionScopedKey(normalizedKey)) {
      originalRemoveItem.call(sessionStorage, normalizedKey);
    }

    originalRemoveItem.call(this, normalizedKey);
  };

  window.__vidiskyStorageProxyInstalled = true;
};
